/**
 * The Play control: a narrated walk through the result, offered under "Ask the community".
 *
 * It appears only once a finished result is on screen, because that is the only time it has
 * anything to say. The rail is 300px, so the scrub is seven segments rather than a time axis -
 * clicking "the polarisation bit" is what anybody actually wants, and scrubbing to 0:43 is not.
 *
 * The caption is not a nicety. Most people browse with the sound off, some cannot use it at all,
 * and the speech service can be unavailable - in which case the tour still runs on a timer and the
 * words are all there is. It is also the second cue for which panel is live, so the green border
 * is never the only thing carrying that.
 */

import { Pause, Play, Volume2 } from "lucide-react";

import { Button } from "@/components/ui/button";

import { useTour } from "./tour";

export function PlayResults() {
  const tour = useTour();
  if (!tour.available) return null;

  const playing = tour.status === "playing";
  const busy = tour.status === "loading";
  const started = tour.clips.length > 0 && (playing || tour.status === "paused");

  return (
    <section className="space-y-2 rounded-xl border border-white/10 bg-[#0f1115] p-3" aria-label="Narrated results">
      <Button
        className="w-full justify-center"
        variant={started ? "outline" : "default"}
        disabled={busy}
        aria-label={playing ? "Pause the narrated results" : "Play the narrated results"}
        onClick={() => (playing ? tour.pause() : started ? tour.resume() : tour.start())}
      >
        {playing ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" className={busy ? "" : "animate-pulse motion-reduce:animate-none"} />}
        {busy ? "Preparing…" : playing ? "Pause" : started ? "Resume" : "Play"}
        <Volume2 aria-hidden="true" />
      </Button>

      {tour.clips.length > 0 && (
        <div className="flex gap-1" role="group" aria-label="Jump to a figure">
          {tour.clips.map((clip, index) => (
            <button
              key={clip.key || "opening"}
              type="button"
              title={clip.title}
              aria-label={`Jump to ${clip.title}`}
              aria-current={index === tour.step}
              onClick={() => tour.jump(index)}
              className={`h-1.5 flex-1 cursor-pointer rounded-full transition-colors ${
                index === tour.step
                  ? "bg-[#0ca30c]"
                  : index < tour.step
                    ? "bg-white/35 hover:bg-white/50"
                    : "bg-white/12 hover:bg-white/25"
              }`}
            />
          ))}
        </div>
      )}

      {started && (
        // Every passage names its own subject, so the title would only say "Backlash" twice.
        <p className="text-xs text-muted-foreground" aria-live="polite">
          {tour.clips[tour.step]?.text}
        </p>
      )}

      {tour.audioError && (
        <p className="text-[11px] text-muted-foreground">
          No sound for this one: {tour.audioError}. The tour still runs, in writing.
        </p>
      )}
    </section>
  );
}
